import { AfterViewInit, Component, HostListener, inject, Input, NgZone, OnDestroy, OnInit } from '@angular/core'
import { NgTemplateOutlet } from '@angular/common'

import {
  slideInRightOnEnterAnimation,
  slideOutRightOnLeaveAnimation,
} from 'angular-animations'

import {
  debounceTime,
  delay, filter, fromEvent, interval, map, merge, of, Subject, switchMap, take, takeUntil, tap,
  timer
} from 'rxjs'

import { BsModalRef, BsModalService, ModalOptions } from 'ngx-bootstrap/modal'
import { MatSnackBarRef } from '@angular/material/snack-bar'
import { MatProgressBarModule } from '@angular/material/progress-bar'

import {
  FrontendCard as Card, CardService,
  StudyPhase,
  UserService,
  DeckService, CardRequest,
  Choice, DeckMode, isRegularStudyPhase,
  StorageService,
  Deck,
} from '@nx-superprep/backend/firebase'

import { _keyCodes, log } from '@nx-superprep/utils'

import {
  ResponsiveComponent,
  SnackbarComponent,
  DotFlashingComponent,
} from '@nx-superprep/shared'

import { GameplayPlayerComponent, nextQuestionDelay } from '@nx-superprep/gameplay'
import { InfoComponent } from './info/info.component'
import { RepositionInterface } from '../shared'
import { LessonTile, StudyAction, StudyExitReason } from '../shared'
import { StudyStateService, StudySessionService, CardFetchingService } from '../shared'
//import { LessonWhisperComponent } from "../lesson-whisper"

@Component({
  selector: 'study-lesson-study',
  standalone: true,
  templateUrl: './lesson-study.component.html',
  styleUrls: ['./lesson-study.component.scss'],
  animations: [
    slideInRightOnEnterAnimation({ duration: 900, delay: 100 }),
    slideOutRightOnLeaveAnimation({ duration: 900, delay: 100 }),
  ],
  imports: [
    NgTemplateOutlet,
    GameplayPlayerComponent,
    DotFlashingComponent,
    InfoComponent,
//    LessonWhisperComponent,
    MatProgressBarModule,
  ],
  providers: [
    BsModalRef,
    BsModalService,
    ModalOptions,
  ]
})
export class LessonStudyComponent extends ResponsiveComponent implements AfterViewInit, OnInit, OnDestroy {
  @Input() moduleId: string | undefined
  @Input() action: StudyAction = 'practice'
  @Input() close: (reason: StudyExitReason) => void = () => this.bsModalRef.hide()
  @Input() lessonTile: LessonTile | undefined

  private _didAnswer = false
  private _snackBarRef: MatSnackBarRef<SnackbarComponent> | undefined
  private _isEndOfStudyHandled = false

  reposition: RepositionInterface = { index: -1 }
  loadingProgress = 0
  isLoaded = false
  isOpenSettings = false
  isSlideVisible = false
  isAutoplay = false
  isWhisper = false
  selected: Choice | undefined

  private readonly zone = inject(NgZone)
  private readonly bsModalRef = inject(BsModalRef)
  private readonly userService = inject(UserService)
  private readonly storageService = inject(StorageService)
  private readonly cardService = inject(CardService)
  private readonly deckService = inject(DeckService)
  private readonly stateService = inject(StudyStateService)
  private readonly sessionService = inject(StudySessionService)
  private readonly cardFetchingService = inject(CardFetchingService)

  constructor() {
    super()
    this.rootClassName = 'lesson-study-root-class-name'
  }

  @HostListener('window:keydown', ['$event'])
  keyEvent(event: Partial<KeyboardEvent>) {
    log.trace(event)
    if (!isRegularStudyPhase(this.phase)) { return }
    if (!event.code || ![_keyCodes.DOWN_ARROW, _keyCodes.RIGHT_ARROW].includes(event.code as _keyCodes)) { return }
    this.nextCard()
  }

  get appSetting() {
    return this.sessionService.appSetting
  }
  get isDebugMode() {
    return this.appSetting.debugMode
  }
  get toolbar() {
    return this.sessionService.toolbar
  }

  //override onBlur(event: FocusEvent): void { if (!this.isDebugMode) { this.endOfStudy({action: 'exit', error: 'study exited onBlur'}) } }
  //override onVisibilityChange(event: Event): void { if (document.visibilityState === 'hidden') { this.endOfStudy({action: 'exit', error: `study exited onVisibilityVhange ${event}`}) } }
  override async onUnload(event: BeforeUnloadEvent) { event.preventDefault(); this.endOfStudy({action: 'unload', error: 'study exited onUnload'}) }
  override async onPopstate(event: PopStateEvent) { if(event) { this.endOfStudy({action: 'unload', error: 'study exited onPopstate'})} }

  override ngOnInit(): void {
    super.ngOnInit()
    this.initializeStudySession()
  }

  ngAfterViewInit(): void {
    this._observeCancelNextCard()
  }

  private initializeStudySession(): void {
    this.isLoaded = false
    log.debug(`${this.action} ${this.moduleId}`)
    if (!this.moduleId) { this.endOfStudy({action:'exit'}); return }

    this.deckService.clear(this.moduleId)

    // Initialize the study session service
    this.sessionService.initialize({
      moduleId: this.moduleId,
      action: this.action,
      onDestroy: this.onDestroy.pipe(map(() => true))
    })

    // Set up event listeners for session
    this._setupSessionListeners()

    // We'll fetch cards only after session is ready
    this._waitForSessionInitialization()
  }

  private _setupSessionListeners(): void {
    // Start background caching after next card cancellation
    this.stateService.cancelNextCard
      .pipe(takeUntil(this.onDestroy), delay(2000), take(1))
      .subscribe(() => {
        setTimeout(() => {
          this.zone.runOutsideAngular(() =>
            this.cardService.backgroundPrefetch(this.moduleId, this.isWeb, this.onCacheProgress)
          )
        }, 0) // Defer caching to next event loop
      })
  }

  private _waitForSessionInitialization(): void {
    // Wait for study settings to be loaded before fetching cards
    // This ensures the session is fully initialized
    const checkInterval = interval(100).pipe(
      takeUntil(this.onDestroy),
      filter(() => !!this.sessionService.studySetting && !!this.sessionService.studySession),
      take(1)
    )

    checkInterval.subscribe(() => {
      log.debug(`${this.moduleId} session initialized, starting card fetch`)
      this.fetchAndProcessCards(this.targetCount())
    })
  }

  // user actions
  onAction(e: string) {
    const [action] = e.split(' ')
    switch (action) {
      case 'home': this.handleHomeAction(); break
      case 'dailyReset': this.sessionService.isDailyReset = !this.sessionService.isDailyReset; break
      case 'openInfos': this.isSlideVisible = !this.isSlideVisible; break
      case 'onFinishCard': this.nextCard(); break
      default: log.debug(`no action for ${e}`)
    }
  }

  private handleHomeAction() {
    const exitReason: StudyExitReason = isRegularStudyPhase(this.phase) ? 'pause' : 'exit';
    const errorMessage = `${this.moduleId} exited by user during ${this.phase}`;
    this.endOfStudy({action: exitReason, error: errorMessage});
  }

  private _totalResources = 0
  private _completedResources = 0
  onCacheProgress(completed: number, total: number) {
    this.zone.run(() => {
      this._completedResources = 1 + completed
      this._totalResources = total || 1 // Handle zero/undefined
      this.loadingProgress = Math.trunc(100 * this._completedResources / this._totalResources)
    })
  }

  private userActivitySubject = new Subject<void>();

  /**
   * Observes cancellation of next card.
   */
  private _observeCancelNextCard() {
    this._setupUserActivityListeners();
    this.stateService.cancelNextCard
      .pipe(
        switchMap(() => {
          log.trace('starting 35s inactivity timer');
          // Create an observable that emits after 35 seconds
          return interval(35*1000).pipe(
            // This will be reset when user activity is detected
            takeUntil(merge(
              this.onDestroy,
              this.sessionService.studyDone.pipe(filter(done => done)),
              // When user activity is detected, cancel this timer
              this.userActivitySubject.pipe(
                tap(() => {
                  log.trace('User activity detected, resetting timer');
                  // Recursively call the same switchMap to restart the timer
                  this.stateService.cancelNextCardOperation();
                  this.stateService.cancelNextCard.next();
                })
              )
            ))
          );
        })
      )
      .subscribe(() => {
        if (!this.isDebugMode) {
          this.endOfStudy({error: '35s timeout reached: ending study session'});
        }
        this.stateService.cancelNextCardOperation();
      });
  }

  // HostListener for keyboard events to reset the timer
  private _setupUserActivityListeners() {
    // Add mouse and touch listeners to the entire component
    const mouseEvents = merge(
      fromEvent<MouseEvent>(document, 'click'),
      fromEvent<MouseEvent>(document, 'mousemove')
    );
    const touchEvents = merge(
      fromEvent<TouchEvent>(document, 'touchstart'),
      fromEvent<TouchEvent>(document, 'touchmove')
    );
    // Combine all events and debounce to avoid too many emissions
    merge(mouseEvents, touchEvents)
      .pipe(
        debounceTime(300),
        takeUntil(merge(this.onDestroy, this.sessionService.studyDone.pipe(filter(done => done)), timer(60000)))
      )
      .subscribe(() => {
        this.userActivitySubject.next();
      });
  }

  // end of study
  endOfStudy({action = 'pause', error}: {action?: StudyExitReason, error?: string | Error}) {
    this.isAutoplay = false
    // Prevent multiple calls to endOfStudy
    if (this._isEndOfStudyHandled) { return Promise.resolve() }
    // end the session and close the modal
    return this.sessionService.endOfStudy({action, error})
      .finally(() => {
        log.debug(`${this.moduleId} closed session ${action}${error instanceof Error ? ' with error' : ''}`);
        if (this.close) { this.close(action) }}
      )
  }

  nextCard(deck = this.deck) {
    // Don't proceed if still answering the current card
    if (this.card && !this.didAnswer) { return; }
    // Cancel any pending timers
    this.stateService.cancelNextCardOperation();
    // Handle case where no deck exists
    if (!deck) {
      this.endOfStudy({ action: 'exit', error: `No deck available` });
      return;
    }
    // Get the result from processAnswer which includes transition info
    const processResult = this.stateService.processAnswerResult;
    // Check if we should transition phases based on the answer processing
    if (processResult?.shouldTransition) {
      log.debug("Transitioning from Review to Acquisition phase");
      // Clear the last answered card before switching
      deck.lastAnsweredCardId = undefined;
      deck.switchToAcquisition();
      this.fetchAndProcessCards();
      return;
    }
    // End study if we've completed daily targets and in regular study phase
    if (this.sessionService.dayPlanModule.isFinished && isRegularStudyPhase(this.phase)) {
      this.endOfStudy({ action: 'complete', error: 'Daily targets reached' });
      return;
    }
    // For acquisition phase specifically, check if we need to fetch more cards
    if (this.phase === StudyPhase.Acquisition) {
      // Check if we've reached the target for acquisition - this is the primary check
      if (deck.isEndOfStudyForAcquisition) {
        log.debug("Acquisition target reached, ending study");
        this.endOfStudy({ action: 'complete', error: 'Acquisition target reached' });
        return;
      }
      // Handle the case when the current card we just answered was the only card or last card
      // in the acquisition deck, so we need new cards
      let currentCard = deck.getCurrentCard();
      if (!currentCard || currentCard.id === deck.lastAnsweredCardId) {
        log.debug(`No current card, fetching more cards`);
        this.fetchAndProcessCards();
        return;
      }
      if (currentCard.id === deck.lastAnsweredCardId) {
        log.debug(`Same current card ${currentCard.id}, discarding`);
        deck.repositionCard(currentCard, this.isCorrect)
        currentCard = deck.getCurrentCard();
        if (!currentCard) {
          log.debug(`No current card, fetching more cards`);
          this.fetchAndProcessCards();
          return;
        }
      }
      // In acquisition phase, we always use the current card
      this.onStudy(currentCard);
      return;
    }
    // Check for regular study completion (other than acquisition which was handled above)
    if (isRegularStudyPhase(this.phase) && deck.isRegularStudyComplete) {
      this.endOfStudy({ action: 'complete', error: 'Regular study complete' });
    }
    // If we have a current card, use that
    const nextCard = deck.getCurrentCard();
    if (nextCard) {
      this.onStudy(nextCard);
      return;
    }
    // Default fallback - fetch more cards
    this.fetchAndProcessCards();
  }

  private createCardRequest(count: number = this.targetCount()): CardRequest {
    // Use current deck phase rather than component phase to ensure
    // phase transition is respected
    const effectivePhase = this.deck?.phase || this.phase;

    return {
      moduleId: this.moduleId || '', // Use component's moduleId as fallback
      studyPhase: effectivePhase, // Use effective phase that reflects deck state
      planDate: this.sessionService.dayPlan?.dateOnly?.toISOString() || new Date().toISOString(),
      acquisitionCardDeck: this.deck?.acquisitionCards.map(c => c.id) ?? [],
      justAcquiredCardId: this.reposition?.justAcquiredCardId,
      cardCount: count,
      maxCardCount: this.targetCount(),
      startReviewDueDateString: this.getStartReviewDueDateString(),
      afterReviewDueDateString: undefined,
      afterCardId: this.getAfterCardId(),
      studyOrderProgress: this.sessionService.studyOrderProgress,
      deckMode: this.deck?.mode ?? DeckMode.Deep,
      reviewCardIds: this.sessionService.dayPlanModule?.reviewCardIds || []
    }
  }

  private fetchAndProcessCards(count: number = this.targetCount()): void {
    // Handle initial case where no deck exists
    if (!this.deck) {
      this.handleCardFetch(count, true, []); // true for initial fetch
      return;
    }

    // If transitioning phases, switch the deck phase before creating request
    if (this.deck.shouldTransitionToAcquisition) {
      log.debug("Phase transition detected in fetchAndProcessCards");
      this.deck.switchToAcquisition();
    }

    // Now create request with current deck phase
    const request = this.createCardRequest(count);
    log.debug(`Fetching ${request.cardCount} ${this.phase} cards with request:`, request);
    this.cardService.fetchCardsByRequest(request)
      .pipe(
        take(1),
        takeUntil(this.onDestroy)
      )
      .subscribe({
        next: (cards) => {
          this.handleCardFetch(count, false, cards); // false for regular fetch
        },
        error: (err) => this.handleFetchError(err)
      });
  }

  private handleCardFetch(count: number, isInitialFetch: boolean, cards: Card[]): void {
    if (isInitialFetch && !this.moduleId) {
      this.endOfStudy({action: 'exit', error: 'No module specified'});
      return;
    }

    // For initial fetch, we need to get the cards first
    if (isInitialFetch) {
      const request = this.createCardRequest(count);
      log.debug(`Fetching ${request.cardCount} ${this.phase} cards with request:`, request);
      this.cardService.fetchCardsByRequest(request)
        .pipe(
          take(1),
          takeUntil(this.onDestroy)
        )
        .subscribe({
          next: (fetchedCards) => this.processCards(fetchedCards, isInitialFetch),
          error: (err) => this.handleFetchError(err)
        });
    } else {
      // For regular fetch, we already have the cards
      this.processCards(cards, isInitialFetch);
    }
  }

  private processCards(cards: Card[], isInitialFetch: boolean): void {
    log.debug(`Processing ${cards.length} fetched cards`);

    // Handle empty card case
    if (cards.length === 0) {
      if (isInitialFetch) {
        this.handleNoCardsAvailable(false);
        return;
      }

      // For regular study phases (Review or Acquisition)
      if (isRegularStudyPhase(this.phase)) {
        this.handleNoCardsAvailable(false);
        return;
      }

      // For non-regular phases (Practice/Quiz), end study
      this.endOfStudy({action: 'exit', error: 'No more cards available'});
      return;
    }

    // Update deck with fetched cards
    const updatedDeck = this.updateDeckWithFetchedCards(cards);

    if (!updatedDeck) {
      log.error("Failed to update deck with fetched cards");
      this.endOfStudy({action: 'exit', error: 'Failed to update deck'});
      return;
    }

    // Check if deck has a current card and log it
    const card = updatedDeck.getCurrentCard();
    log.debug("Current card after update:", card?.id || "No current card");

    if (card) {
      this.onStudy(card);
    } else {
      if (updatedDeck.resetIndexes()) {
        const recoveredCard = updatedDeck.getCurrentCard();
        if (recoveredCard) {
          log.warn(`Recovered current card after index reset: ${recoveredCard.id}`);
          this.onStudy(recoveredCard);
          return;
        }
      }
      log.error("No current card available after deck update and reset attempt", {
        deckSize: updatedDeck.cards.length,
        reviewCards: updatedDeck.reviewCards.length,
        acquisitionCards: updatedDeck.acquisitionCards.length,
        phase: updatedDeck.phase
      });

      // Try recovery or end study
      this.endOfStudy({
        action: 'exit',
        error: 'No current card available despite recovery attempt'
      });
    }
  }

  private handleFetchError(error: Error | unknown) {
    log.error('Card fetch error:', error);

    // Special handling for initial deck creation failure
    if (!this.deck) {
      this.endOfStudy({action: 'exit', error: 'Initial deck creation failed'});
      return;
    }

    // For regular study, try continuing with current phase
    if (isRegularStudyPhase(this.phase)) {
      this.fetchAndProcessCards()
    } else {
      this.endOfStudy({action: 'exit', error: error instanceof Error ? error : String(error)});
    }
  }

  private handleNoCardsAvailable(wasPhaseTransition: boolean): void {
    const deck = this.deck;

    if (!deck) {
      this.endOfStudy({action: 'exit', error: 'No deck available'});
      return;
    }

    // If we just transitioned to Acquisition but got no cards,
    // we should check if acquisition is really done
    if (wasPhaseTransition && deck.phase === StudyPhase.Acquisition) {
      if (!deck.isEndOfStudyForAcquisition) {
        log.debug("No cards returned after phase transition, retrying");
        this.fetchAndProcessCards()
        return;
      }
    }

    // Handle Review phase
    if (deck.phase === StudyPhase.Review) {
      if (deck.isEndOfStudyForReview && !deck.dayPlanModule.isAcquisitionFinished) {
        log.debug("No cards available in Review, transitioning to Acquisition");
        deck.switchToAcquisition();
        this.fetchAndProcessCards();
        return;
      }
    }

    // Handle Acquisition phase
    if (deck.phase === StudyPhase.Acquisition) {
      if (deck.isEndOfStudyForAcquisition) {
        // Acquisition complete - end study
        this.endOfStudy({action: 'complete', error: 'Study complete'});
        return;
      }
      log.debug("Acquisition phase not complete, retrying card fetch");
      this.fetchAndProcessCards();
      return;
    }

    // For non-regular phases (Practice/Quiz)
    if (!isRegularStudyPhase(this.phase)) {
      this.endOfStudy({action: 'exit', error: `${this.phase} session complete`});
      return;
    }

    // Default fallback
    this.fetchAndProcessCards();
  }

  // Helper function to get the maximum card count based on the study phase
  private targetCount(): number {
    return this.deck
    ? this.deck.toTargetLength
    : (isRegularStudyPhase(this.phase)
      ? this.phase === StudyPhase.Acquisition
        ? this.sessionService.dayPlanModule.acquisitionTarget
        : this.sessionService.dayPlanModule.reviewTarget
      : 10
    )
  }

  // Helper function to get the start review due date string
  private getStartReviewDueDateString(): string {
    return this.phase === StudyPhase.Review
      ? (this.sessionService.dayPlan.dateOnly ?? new Date()).toISOString() // Date for review phase
      : ''
  }

  // Helper function to get the after card ID based on the study phase
  private getAfterCardId(): string | undefined {
    return isRegularStudyPhase(this.phase)
      ? undefined
      : this.deck?.currentCardIds(true).pop() // ID of the last card for non-regular phases
  }

  // Helper function to update the deck with fetched cards
  private updateDeckWithFetchedCards(cards: Card[]): Deck | undefined {
    return this.deckService.updateData({
      cards,
      setting: this.sessionService.studySetting,
      dayPlanModule: this.sessionService.dayPlanModule,
      spacedRepConfig: this.sessionService.spacedRepConfig,
      studyPhase: this.phase,
      reviewIds: this.sessionService.dayPlanModule.reviewCardIds,
      lastAnsweredCardId: this.reposition?.lastAnsweredCardId,
    })
  }

  //-------------------------------------------------------------------------------------------------
  // post answer update
  //-------------------------------------------------------------------------------------------------

  update(card: Card, choice: Choice): Promise<void> {
    // Return immediately if no active user
    if (!this.userService.activeUser) { return Promise.resolve() }

    return this.stateService.processCardUpdate(
      card,
      choice,
      {
        responseTime: this.stateService.responseTimerValue,
        sessionTime: this.stateService.sessionTimerValue,
        phase: this.phase,
        spacedRep: this.sessionService.spacedRepConfig,
        studySetting: this.sessionService.studySetting,
        session: this.sessionService.studySession,
        module: this.sessionService.dayPlanModule,
        plan: this.sessionService.dayPlan,
        studyOrderProgress: this.sessionService.studyOrderProgress,
        reposition: this.reposition,
      }
    )
  }

  //-------------------------------------------------------------------------------------------------
  // gameplay
  //-------------------------------------------------------------------------------------------------

  // helpers

  get isReady() {
    return this.appSetting && this.setting && this.card
  }

  get deck() {
    return this.deckService.deck
  }

  get phase(): StudyPhase {
    return this.sessionService.phase
  }

  get progress() {
    return this.deck?.progress ?? 1
  }

  get card() {
    return this.deckService.selectedCard.value
  }
  set card(value) {
    if (value && this.reposition.lastAnsweredCardId === value.id) { log.warn(`same ${this.phase} card ${value.id} picked twice`) } else { log.debug(`new ${this.phase} card ${value?.id}`) }
    this.deckService.selectedCard.next(value)
    this.didAnswer = false
    this.isLoaded = true
    this.stateService.startResponseTimer()
  }

  // player selector

  private _useTSX = true // default to TSX
  get useTSX() { return this._useTSX }
  set useTSX(value) { setTimeout(() => this._useTSX = value, 0)}

  // study

  get setting() {
    return this.sessionService.studySetting
  }

  get duration() {
    return this.setting.duration
  }

  get difficulty() {
    return this.setting.difficulty
  }

  get didAnswer() {
    return this._didAnswer
  }
  set didAnswer(value) {
    this._didAnswer = value
  }

  public isCorrect = false

  public get question() {
    return this.card?.template.question
  }

  public get answer() {
    return this.card?.template.answer
  }

  get rightAnswerTime() {
    return this.sessionService.spacedRepConfig?.rightAnswerDisplayTime[this.difficulty] || 1
  }
  get wrongAnswerTime() {
    return this.sessionService.spacedRepConfig?.wrongAnswerDisplayTime[this.difficulty] || 1
  }

  onStudy(card: Card): void {
    if (this.didAnswer) { this._snackBarRef?.dismiss() }
    this.cardService.assetizeCards([card], false, 4).then(cr => this.zone.run(() => {this.card = cr.cards[0] as Card}))
  }

  onSelect(choice: Choice | undefined) {
    this.stateService.stopResponseTimer()
    if (this.isAutoplay) { this.stateService.responseTimerValue = Math.random() * 3 }
    choice = choice ?? this.card?.template.choices.find((c) => !c.isAnswer)
    if (!choice) { return }
    this._processAnswer(choice)
    log.debug('selected', choice)
  }

  private _processAnswer(choice: Choice) {
    if (!this.userService.activeUser || this.didAnswer || !this.card || !this.answer) { return }
    this.stateService.cancelNextCardOperation()
    this.didAnswer = true
    this.isCorrect = choice.isAnswer
    this.update(this.card, choice).finally(async () => {
      if (this.useTSX) { return } //react gameplays handle the post answer timer internally
      const answer = this.answer
      const audioDuration = (answer?.audio && (choice.isWrong || this.card?.gameplay.alwaysPlayPostAnswerAudios))
        ? await this.storageService.getAudioDuration(answer.audio.name)
        : 0
      const _delay = nextQuestionDelay(
        this.isCorrect,
        this.isAutoplay ? 0.5 : this.rightAnswerTime,
        this.isAutoplay ? 0.5 : this.wrongAnswerTime,
        this.setting.animation,
        this.setting.difficulty,
        audioDuration,
      ) + (this.progress < 1 ? 0 : this.isAutoplay ? 100 : 400)
      of(true)
      .pipe(
          delay(_delay),take(1),takeUntil(merge(this.onDestroy, this.stateService.cancelNextCard
        )),
          tap(() => log.debug(`next question after ${(_delay/1000).toFixed(2)}`))
        )
      .subscribe(() => this.onAction('onFinishCard'))
    })
  }
}
